import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad } from "@redotech/react-util/load";
import { StorefrontClientContext } from "@redotech/redo-merchant-app-common/team";
import { BadgeRedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { getAllCollections } from "@redotech/shopify-storefront/collections";
import { parseNumericIdFromShopifyId } from "@redotech/shopify-util/id";
import { memo, useMemo, useState } from "react";

export const CollectionSelect = memo(function CollectionSelect({
  value,
  valueChange,
}: {
  value: string | undefined;
  valueChange: (value: string) => void;
}) {
  const storefrontClient = useRequiredContext(StorefrontClientContext);

  const [searchString, setSearchString] = useState("");

  const collectionsLoad = useLoad(async () => {
    return (await getAllCollections(storefrontClient)).map((collection) => ({
      id: parseNumericIdFromShopifyId(collection.id),
      title: collection.title,
    }));
  }, [storefrontClient]);

  const options: BadgeRedoListItem<{ value: string; label: string }>[] =
    useMemo(() => {
      if (!collectionsLoad.value) {
        return [];
      }

      return collectionsLoad.value
        .filter(
          (collection) =>
            !searchString ||
            collection.title.toLowerCase().includes(searchString.toLowerCase()),
        )
        .map((collection) => ({
          id: collection.id,
          type: "badge",
          value: { value: collection.id, label: collection.title },
          badge: { text: collection.title },
        }));
    }, [collectionsLoad.value, searchString]);

  const selectedOption:
    | BadgeRedoListItem<{ value: string; label: string }>
    | undefined = useMemo(() => {
    if (!value) {
      return undefined;
    }

    return options.find((option) => option.value.value === value);
  }, [value, options]);

  return (
    <RedoSingleSelectDropdownInput
      itemsLoading={collectionsLoad.pending}
      options={options}
      optionSelected={(args) => {
        valueChange(args.item.value.value);
      }}
      placeholder="Select a collection"
      searchPlaceholder="Search for a collection"
      searchString={searchString}
      selectedItem={selectedOption}
      setSearchString={setSearchString}
      size="xs"
    />
  );
});
